import * as React from 'react';
import { toast } from 'react-toastify';
import { ToastComponent, ToastType, ToastOptions } from '@common/components';

import { actions } from 'react-redux-form';
import { Action, Dispatch } from 'redux';

import { AppState } from '..';
import { PageViewModel } from '@models/common.models';
import { contractService } from '@services/ContractService';
import { ContractSimpleViewModel } from '@models/Contract';
import { TransactionCoordinatorState } from './transaction-coordinator.reducer';

import { transactionCoordinatorService } from '@services/TransactionCoordinatorService';
import { TransactionCoordinatorViewModel, TransactionCoordinatorSearchViewModel } from '@models/TransactionCoordinator';
import { realEstateOfficesService } from '@services/RealEstateOfficesService';
import { RealEstateOfficeViewModel } from '@models/RealEstateOffice';
import { history } from '../index';
import { RouteDefinitions } from '@common/route-definitions';
import { getRoute } from '../common/route-definitions';

// We can use enums instead of type constants like in the example. This is a newer TS feature
export enum TransactionCoordinatorActionType {
  /********** Save **********/
  SaveTransactionCoordinatorStart = 'SaveTransactionCoordinatorStart',
  SaveTransactionCoordinatorSuccess = 'SaveTransactionCoordinatorSuccess',
  SaveTransactionCoordinatorFailure = 'SaveTransactionCoordinatorFailure',

  /********** Get **********/
  GetTransactionCoordinatorStart = 'GetTransactionCoordinatorStart',
  GetTransactionCoordinatorSuccess = 'GetTransactionCoordinatorSuccess',
  GetTransactionCoordinatorFailure = 'GetTransactionCoordinatorFailure',

  GetTransactionCoordinatorDetailStart = 'GetTransactionCoordinatorDetailStart',
  GetTransactionCoordinatorDetailSuccess = 'GetTransactionCoordinatorDetailSuccess',
  GetTransactionCoordinatorDetailFailure = 'GetEscrowOfficeDetailFailure',

  GetContractsByCoordinatorStart = 'GetContractsByCoordinatorStart',
  GetContractsByCoordinatorSuccess = 'GetContractsByCoordinatorSuccess',
  GetContractsByCoordinatorFailure = 'GetContractsByCoordinatorFailure',

  /******* Search **********/
  TransactionCoordinatorSearchInit = 'TransactionCoordinatorSearchInit',
  TransactionCoordinatorSearchSuccess = 'TransactionCoordinatorSearchSuccess',
  TransactionCoordinatorSearchFailure = 'TransactionCoordinatorSearchFailure',

  ClearTransactionCoordinatorState = 'ClearTransactionCoordinatorState'
}

// This is defining the action definition for certain actions on the page.
// There may be multiple types of these on a given page.
// All properties should be readonly.

/********** Save **********/
export interface SaveTransactionCoordinatorStartAction extends Action {
  readonly type: TransactionCoordinatorActionType.SaveTransactionCoordinatorStart;
}

export interface SaveTransactionCoordinatorSuccessAction extends Action {
  readonly type: TransactionCoordinatorActionType.SaveTransactionCoordinatorSuccess;
  readonly currentId: number;
}

export interface SaveTransactionCoordinatorFailureAction extends Action {
  readonly type: TransactionCoordinatorActionType.SaveTransactionCoordinatorFailure;
  readonly errorMessage: string;
}

/********** Get **********/
export interface GetTransactionCoordinatorStartAction extends Action {
  readonly type: TransactionCoordinatorActionType.GetTransactionCoordinatorStart;
}

export interface GetTransactionCoordinatorSuccessAction extends Action {
  readonly type: TransactionCoordinatorActionType.GetTransactionCoordinatorSuccess;
  readonly txCoordinator: TransactionCoordinatorViewModel;
  readonly realEstateOffice: RealEstateOfficeViewModel;
}

export interface GetTransactionCoordinatorFailureAction extends Action {
  readonly type: TransactionCoordinatorActionType.GetTransactionCoordinatorFailure;
  readonly errorMessage: string;
}

export interface GetTransactionCoordinatorDetailStartAction extends Action {
  readonly statusMessage: string;
  readonly type: TransactionCoordinatorActionType.GetTransactionCoordinatorDetailStart;
}

export interface GetTransactionCoordinatorDetailSuccessAction extends Action {
  readonly statusMessage: string;
  readonly transactionCoordinatorVM: TransactionCoordinatorViewModel;
  readonly type: TransactionCoordinatorActionType.GetTransactionCoordinatorDetailSuccess;
}

export interface GetTransactionCoordinatorDetailFailureAction extends Action {
  readonly statusMessage: string;
  readonly errorMessage: string;
  readonly type: TransactionCoordinatorActionType.GetTransactionCoordinatorDetailFailure;
}

export interface GetContractsByCoordinatorStartAction extends Action {
  readonly statusMessage: string;
  readonly type: TransactionCoordinatorActionType.GetContractsByCoordinatorStart;
}

export interface GetContractsByCoordinatorSuccessAction extends Action {
  readonly statusMessage: string;
  readonly type: TransactionCoordinatorActionType.GetContractsByCoordinatorSuccess;
  readonly result: PageViewModel<ContractSimpleViewModel>;
}

export interface GetContractsByCoordinatorFailureAction extends Action {
  readonly statusMessage: string;
  readonly errorMessage: string;
  readonly type: TransactionCoordinatorActionType.GetContractsByCoordinatorFailure;
}

/********** Search **********/
export interface TransactionCoordinatorSearchInitAction extends Action {
  readonly query: string | null;
  readonly type: TransactionCoordinatorActionType.TransactionCoordinatorSearchInit;
}
export interface TransactionCoordinatorSearchSuccessAction extends Action {
  readonly pageNumber: number;
  readonly pageSize: number;
  readonly orderBy: string;
  readonly result: PageViewModel<TransactionCoordinatorSearchViewModel>;
  readonly type: TransactionCoordinatorActionType.TransactionCoordinatorSearchSuccess;
}
export interface TransactionCoordinatorSearchFailureAction extends Action {
  readonly errorMessage: string;
  readonly type: TransactionCoordinatorActionType.TransactionCoordinatorSearchFailure;
}

export interface ClearTransactionCoordinatorStateAction extends Action {
  readonly type: TransactionCoordinatorActionType.ClearTransactionCoordinatorState;
}

// Actions for each action type declared here.
// They should all return an action object.

/********** Save **********/
export const saveTransactionCoordinator = (model: TransactionCoordinatorViewModel, saveCallback?: Function) => {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(saveTransactionCoordinatorStart());
    try {
      const id = await transactionCoordinatorService.saveTransactionCoordinator(model);
      if (saveCallback) {
        saveCallback(id);
        dispatch(saveTransactionCoordinatorSuccess(id, true));
        dispatch(clearTransactionCoordinatorState());
      } else {
        dispatch(saveTransactionCoordinatorSuccess(id));
        dispatch(clearTransactionCoordinatorState());
      }
    } catch (e) {
      dispatch(saveTransactionCoordinatorFailure(e.toString()));
    }
  };
};

export function saveTransactionCoordinatorStart(): SaveTransactionCoordinatorStartAction {
  return {
    type: TransactionCoordinatorActionType.SaveTransactionCoordinatorStart
  };
}

export function saveTransactionCoordinatorSuccess(
  id: number,
  noForward?: boolean
): SaveTransactionCoordinatorSuccessAction {
  toast.success(
    <ToastComponent type={ToastType.Success} message={'Transaction Coordinator Saved Successfully'} />,
    ToastOptions.Success
  );
  if (!noForward) {
    history.push(getRoute(RouteDefinitions.transactioncoordinator.Details, { id: id }));
  }

  return {
    type: TransactionCoordinatorActionType.SaveTransactionCoordinatorSuccess,
    currentId: id
  };
}

export function saveTransactionCoordinatorFailure(errorMessage: string): SaveTransactionCoordinatorFailureAction {
  toast.error(<ToastComponent type={ToastType.Error} message={errorMessage} />, ToastOptions.Error);
  return {
    type: TransactionCoordinatorActionType.SaveTransactionCoordinatorFailure,
    errorMessage: errorMessage
  };
}

/********** Get **********/
export function getTransactionCoordinator(id?: number, onSuccess?: Function) {
  return async (dispatch: Dispatch<AppState>) => {
    // dispatch(actions.reset('escrowInfoForm'));
    // Reset Escrow Info Section
    dispatch(actions.change('affiliatesInfoForm.transCoordId', 0));

    if (id) {
      dispatch(getTransactionCoordinatorStart());
      try {
        var transactionCoordinatorVM = await transactionCoordinatorService.getTransactionCoordinator(id);
        var realEstateOfficeVM =
          !onSuccess && transactionCoordinatorVM.officeId
            ? await realEstateOfficesService.get(transactionCoordinatorVM.officeId)
            : ({} as RealEstateOfficeViewModel);

        const model = {
          ...transactionCoordinatorVM,
          realEstateOfficeVM
        };
        if (onSuccess) {
          dispatch(actions.change('affiliatesInfoForm.transCoordId', transactionCoordinatorVM.id));
          return dispatch(onSuccess(transactionCoordinatorVM));
        } else {
          dispatch(actions.change('transactionCoordinatorForm', model));
          return dispatch(getTransactionCoordinatorSuccess(transactionCoordinatorVM, realEstateOfficeVM));
        }
      } catch (e) {
        dispatch(getTransactionCoordinatorFailure(e.toString()));
      }
    }
  };
}

export function getTransactionCoordinatorStart(): GetTransactionCoordinatorStartAction {
  return {
    type: TransactionCoordinatorActionType.GetTransactionCoordinatorStart
  };
}

export function getTransactionCoordinatorSuccess(
  txCoordinator: TransactionCoordinatorViewModel,
  officeVM: RealEstateOfficeViewModel
): GetTransactionCoordinatorSuccessAction {
  return {
    type: TransactionCoordinatorActionType.GetTransactionCoordinatorSuccess,
    txCoordinator: txCoordinator,
    realEstateOffice: officeVM
  };
}

export function getTransactionCoordinatorFailure(errorMessage: string): GetTransactionCoordinatorFailureAction {
  toast.error(
    <ToastComponent
      type={ToastType.Error}
      message={'Network error, unable to get transaction coordinator. ' + errorMessage}
    />,
    ToastOptions.Error
  );

  return {
    type: TransactionCoordinatorActionType.GetTransactionCoordinatorFailure,
    errorMessage: errorMessage
  };
}

export function lookupOffice(value: string, id?: number) {
  return realEstateOfficesService.search(id ? [id] : undefined, undefined, undefined, 1, 100, value);
}

export function updateOfficeId(value: number) {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(actions.change('transactionCoordinatorForm.officeId', value));
  };
}

export function getTransactionCoordinatorDetail(id: number) {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(getTransactionCoordinatorDetailStart());
    try {
      const transactionCoordinatorVM = await transactionCoordinatorService.getTransactionCoordinator(id);
      dispatch(getTransactionCoordinatorDetailSuccess(transactionCoordinatorVM));
    } catch (e) {
      dispatch(getTransactionCoordinatorDetailFailure(e.toString()));
    }
  };
}

export function getTransactionCoordinatorDetailStart(): GetTransactionCoordinatorDetailStartAction {
  // do something with the escrowofficer
  return {
    statusMessage: 'Getting Escrow Officer Detail',
    type: TransactionCoordinatorActionType.GetTransactionCoordinatorDetailStart
  };
}

export function getTransactionCoordinatorDetailSuccess(
  officer: TransactionCoordinatorViewModel
): GetTransactionCoordinatorDetailSuccessAction {
  // do something with the escrowofficer
  return {
    type: TransactionCoordinatorActionType.GetTransactionCoordinatorDetailSuccess,
    transactionCoordinatorVM: officer,
    statusMessage: 'Officer: ' + officer.id + 'loaded.'
  };
}

export function getTransactionCoordinatorDetailFailure(err: string): GetTransactionCoordinatorDetailFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Network error, unable to get transaction coordinator. ' + err} />,
    ToastOptions.Error
  );

  return {
    type: TransactionCoordinatorActionType.GetTransactionCoordinatorDetailFailure,
    statusMessage: 'Failed to load officer.',
    errorMessage: err
  };
}

export function getContractsByCoordinator(coordinatorId: number) {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(getContractsByCoordinatorStart());
    try {
      const result: PageViewModel<ContractSimpleViewModel> = await contractService.searchByTransactionCoordinator(
        coordinatorId
      );
      dispatch(getContractsByCoordinatorSuccess(result));
    } catch (e) {
      dispatch(getContractsByCoordinatorFailure(e.toString()));
    }
  };
}

export function getContractsByCoordinatorStart(): GetContractsByCoordinatorStartAction {
  return {
    statusMessage: 'Getting Escrow Officer Contracts',
    type: TransactionCoordinatorActionType.GetContractsByCoordinatorStart
  };
}

export function getContractsByCoordinatorSuccess(
  result: PageViewModel<ContractSimpleViewModel>
): GetContractsByCoordinatorSuccessAction {
  return {
    statusMessage: 'Got the contracts',
    type: TransactionCoordinatorActionType.GetContractsByCoordinatorSuccess,
    result: result
  };
}

export function getContractsByCoordinatorFailure(errorMessage: string): GetContractsByCoordinatorFailureAction {
  toast.error(
    <ToastComponent
      type={ToastType.Error}
      message={'Network error, unable to get contracts by coordinator. ' + errorMessage}
    />,
    ToastOptions.Error
  );

  return {
    statusMessage: 'error',
    errorMessage: errorMessage,
    type: TransactionCoordinatorActionType.GetContractsByCoordinatorFailure
  };
}

/********** Search **********/
export const transactionCoordinatorSearch = (
  query: string | null,
  pageSize: number,
  pageNumber: number,
  orderBy: string,
  orderDirection: string
) => {
  return async (dispatch: Dispatch<TransactionCoordinatorState>) => {
    dispatch(transactionCoordinatorSearchInit(query));
    try {
      let result: PageViewModel<
        TransactionCoordinatorSearchViewModel
      > = await transactionCoordinatorService.searchTransactionCoordinatorsFull(
        query,
        pageSize,
        pageNumber,
        orderBy,
        orderDirection
      );
      dispatch(transactionCoordinatorSearchSuccess(pageNumber, pageSize, orderBy, result));
    } catch (e) {
      dispatch(transactionCoordinatorSearchFailure(e.toString()));
    }
  };
};
export function transactionCoordinatorSearchInit(query: string | null): TransactionCoordinatorSearchInitAction {
  return {
    query: query,
    type: TransactionCoordinatorActionType.TransactionCoordinatorSearchInit
  };
}
export function transactionCoordinatorSearchFailure(errorMessage: string): TransactionCoordinatorSearchFailureAction {
  toast.error(
    <ToastComponent
      type={ToastType.Error}
      message={'Network error, unable to get transaction coordinators. ' + errorMessage}
    />,
    ToastOptions.Error
  );

  return {
    errorMessage: errorMessage,
    type: TransactionCoordinatorActionType.TransactionCoordinatorSearchFailure
  };
}
export function transactionCoordinatorSearchSuccess(
  pageNumber: number,
  pageSize: number,
  orderBy: string,
  result: PageViewModel<TransactionCoordinatorSearchViewModel>
): TransactionCoordinatorSearchSuccessAction {
  return {
    pageNumber: pageNumber,
    pageSize: pageSize,
    orderBy: orderBy,
    result: result,
    type: TransactionCoordinatorActionType.TransactionCoordinatorSearchSuccess
  };
}

export function clearTransactionCoordinatorState(): ClearTransactionCoordinatorStateAction {
  return {
    type: TransactionCoordinatorActionType.ClearTransactionCoordinatorState
  };
}



// WEBPACK FOOTER //
// ./src/transaction-coordinator/transaction-coordinator.actions.tsx